import { Fragment, type ComponentType } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip'
import { cn } from '~/lib/utils'
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs'

export interface AppTab<TValue extends string> {
  /** Séparateur vertical avant l'onglet (groupes d'onglets). */
  separatorBefore?: boolean
  /** Icône lucide, ou tout composant qui accepte `className` (icône de maquette). */
  icon?: ComponentType<{ className?: string }>
  label: React.ReactNode
  value: TValue
  /** Onglet inaccessible : désactivé (jamais masqué), cf. `useAuthorizedTabs`. */
  disabled?: boolean
  /** Explication affichée au survol quand l'onglet est désactivé (droits manquants). */
  tooltip?: React.ReactNode
}

interface AppTabsProps<TValue extends string> {
  tabs: AppTab<TValue>[]
  /** Onglet actif ; `null` : aucun (onglets portés par l'URL, sur une route sans onglet). */
  value?: TValue | null
  defaultValue?: TValue
  onTabChange?: (value: TValue) => void
  className?: string
  tabsClassName?: string
  tabsListClassName?: string
  readonly?: boolean
  /** `pill` : onglets compacts, l'actif plein sur fond vert (barre de la maquette Logistique). */
  variant?: 'default' | 'pill'
  /**
   * `compact` : pastilles de 28 px sur un cadre de 8 px (onglets d'un panneau). Sans effet hors de
   * la variante `pill`.
   */
  size?: 'default' | 'compact'
  children?: React.ReactNode
}

const PILL_TABS_LIST_CLASS = cn(
  'h-auto gap-0 rounded-2xl border-black/6 bg-sidebar p-[5px]',
  '[&_[data-slot=tabs-trigger]]:h-10 [&_[data-slot=tabs-trigger]]:flex-none [&_[data-slot=tabs-trigger]]:gap-2',
  '[&_[data-slot=tabs-trigger]]:rounded-xl [&_[data-slot=tabs-trigger]]:border-0 [&_[data-slot=tabs-trigger]]:px-3 [&_[data-slot=tabs-trigger]]:py-0',
  '[&_[data-slot=tabs-trigger]]:text-[13px] [&_[data-slot=tabs-trigger]]:font-semibold [&_[data-slot=tabs-trigger]]:text-[#3e4f3f]/60',
  '[&_[data-slot=tabs-trigger]:not([data-active]):hover]:text-[#3e4f3f] [&_[data-slot=tabs-trigger]:disabled]:opacity-100',
  '[&_[data-slot=tabs-trigger][data-active]]:bg-[#4f6a47] [&_[data-slot=tabs-trigger][data-active]]:text-white',
)

/** `pill` en taille `compact` (maquette du détail d'une tournée) : inactifs en vert plein. */
const PILL_COMPACT_TABS_LIST_CLASS = cn(
  'rounded-lg',
  '[&_[data-slot=tabs-trigger]]:h-7 [&_[data-slot=tabs-trigger]]:gap-1.5 [&_[data-slot=tabs-trigger]]:rounded-md [&_[data-slot=tabs-trigger]]:px-2',
  '[&_[data-slot=tabs-trigger]]:text-sm [&_[data-slot=tabs-trigger]]:text-primary-600 [&_[data-slot=tabs-trigger]:not([data-active]):hover]:text-primary-700',
  '[&_[data-slot=tabs-trigger][data-active]]:text-sidebar',
)

export const AppTabs = <TValue extends string>({
  tabs,
  value,
  className,
  tabsClassName,
  tabsListClassName,
  onTabChange,
  defaultValue,
  readonly = false,
  variant = 'default',
  size = 'default',
  children,
}: AppTabsProps<TValue>) => (
  <Tabs
    defaultValue={defaultValue}
    value={value}
    onValueChange={(v: TValue) => onTabChange?.(v)}
    className={tabsClassName}
  >
    <TabsList
      className={cn(
        variant === 'pill' && PILL_TABS_LIST_CLASS,
        variant === 'pill' && size === 'compact' && PILL_COMPACT_TABS_LIST_CLASS,
        tabsListClassName ?? className,
      )}
    >
      {tabs.map((tab) => {
        const trigger = (
          <TabsTrigger
            value={tab.value}
            disabled={readonly || tab.disabled}
            className={cn(
              readonly &&
                'pointer-events-none hover:text-inherit text-white text-sm not-italic font-medium leading-4 font-sans',
            )}
          >
            {tab.icon && <tab.icon className="size-4" />}
            {tab.label}
          </TabsTrigger>
        )

        return (
          <Fragment key={`${tab.value}-tab`}>
            {tab.separatorBefore && (
              <span aria-hidden="true" className="mx-2 h-6 w-px shrink-0 bg-black/6" />
            )}
            {/* Un trigger désactivé n'émet pas d'évènement pointeur : le `<span>` porte le tooltip. */}
            {tab.tooltip ? (
              <Tooltip>
                <TooltipTrigger render={<span tabIndex={0} />}>{trigger}</TooltipTrigger>
                <TooltipContent>{tab.tooltip}</TooltipContent>
              </Tooltip>
            ) : (
              trigger
            )}
          </Fragment>
        )
      })}
    </TabsList>
    {children}
  </Tabs>
)
